import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, Minus, Plus, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  fetchPricingPlans,
  fetchDisplaySettings,
  fetchBillingSubscription,
  previewDowngrade,
  previewProration,
  scheduleDowngrade,
  upgradePlan,
  startSkipCashPayment,
  pollPaymentOrder,
  cancelCatalogAddon,
  setPlanAutoRenew,
  setAddonAutoRenew,
} from '../lib/customerBillingApi';
import {
  INTERNAL_TO_SLUG,
  PLAN_TIER,
  slugToInternal,
  formatUsdFromCents,
  yearlyMonthlyCents,
  savingsPercent,
  planCtaLabel,
  includedFeatures,
  addonsForPlan,
  showAddonsSection,
  centsToUsd,
  isCatalogAddonActive,
  getCatalogAddonStatus,
  ownedAddonQuantity,
  tieredAddonUnitCents,
  addonLineAmountCents,
} from '../lib/pricingUtils';
import UpgradePlanDialog from '../components/billing/customer/UpgradePlanDialog';
import DowngradePlanDialog from '../components/billing/customer/DowngradePlanDialog';
import PricingCheckoutPanel from '../components/billing/customer/PricingCheckoutPanel';
import BillingHistoryDialog from '../components/billing/customer/BillingHistoryDialog';

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [freeTrialDays, setFreeTrialDays] = useState(14);
  const [displaySettings, setDisplaySettings] = useState({});
  const [subscription, setSubscription] = useState(null);

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlanSlug, setSelectedPlanSlug] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [addonQuantities, setAddonQuantities] = useState({});

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const [downgradePreview, setDowngradePreview] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const internalPlanId = subscription?.plan_id || 'free';
  const currentSlug = INTERNAL_TO_SLUG[internalPlanId] || null;
  const isTrialing = subscription?.status === 'trialing';
  // Paid plan whose period lapsed or whose renewal failed. Combine API flag with a
  // client-side period check so an expired Scaling plan never shows as "Current Plan".
  const statusLower = (subscription?.status || '').toLowerCase();
  const periodEnded = Boolean(
    subscription?.current_period_end &&
      !Number.isNaN(new Date(subscription.current_period_end).getTime()) &&
      new Date(subscription.current_period_end).getTime() <= Date.now()
  );
  const renewalRequired = Boolean(
    subscription &&
      internalPlanId !== 'free' &&
      !subscription.billing_exempt &&
      (subscription.renewal_required === true ||
        subscription.billing_suspended ||
        subscription.requires_payment_to_restore ||
        ['past_due', 'unpaid', 'canceled', 'cancelled', 'expired'].includes(statusLower) ||
        periodEnded)
  );
  const isEnterprise = internalPlanId === 'corporate';
  const paymentsEnabled = Boolean(displaySettings.payments_enabled);
  const stripeEnabled = Boolean(displaySettings.stripe_enabled);

  const effectivePlanSlug =
    selectedPlanSlug || currentSlug || searchParams.get('highlight') || 'growing';

  const selectedPlan = plans.find((p) => p.id === effectivePlanSlug) || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const catalog = await fetchPricingPlans();
      let settings = {};
      try {
        settings = await fetchDisplaySettings();
      } catch {
        settings = {
          payments_enabled: catalog.payments_enabled,
          stripe_enabled: catalog.stripe_enabled,
          contact_sales_email: catalog.contact_sales_email,
        };
      }
      setPlans(catalog.plans || []);
      setAddons(catalog.addons || []);
      setFreeTrialDays(catalog.free_trial_days ?? 14);
      setDisplaySettings({
        ...settings,
        payments_enabled: settings.payments_enabled ?? catalog.payments_enabled,
        stripe_enabled: settings.stripe_enabled ?? catalog.stripe_enabled,
        contact_sales_email: settings.contact_sales_email ?? catalog.contact_sales_email,
      });
    } catch (e) {
      console.error('Pricing catalog load failed:', e);
      toast.error('Could not load pricing. Is the API running on port 8000?');
      setLoading(false);
      return;
    }

    if (currentOrg) {
      try {
        const sub = await fetchBillingSubscription(currentOrg.id);
        setSubscription(sub);
        const bc = (sub.billing_cycle || 'monthly').toLowerCase();
        setBillingCycle(bc === 'yearly' ? 'yearly' : 'monthly');
        setSelectedPlanSlug((prev) => {
          if (prev) return prev;
          const slug = INTERNAL_TO_SLUG[sub.plan_id];
          if (slug && slug !== 'enterprise') return slug;
          if ((sub.status || '').toLowerCase() === 'trialing') return 'growing';
          return prev;
        });
      } catch (e) {
        console.warn('Subscription load failed (catalog still shown):', e);
        setSubscription(null);
      }
    } else {
      setSubscription(null);
      setSelectedPlanSlug((prev) => prev || 'growing');
    }
    setLoading(false);
  }, [currentOrg]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const orderId = searchParams.get('order');
    if (payment === 'success') {
      (async () => {
        if (orderId) {
          try {
            await pollPaymentOrder(orderId);
          } catch {
            /* ignore */
          }
        }
        setConfirmed(true);
        toast.success("You're all set! Your subscription is updated.");
        if (currentOrg) await load();
        setAddonQuantities({});
        setSelectedAddons({});
        setSearchParams({}, { replace: true });
      })();
    } else if (payment === 'pending') {
      toast.warning('Payment is still processing. Wait a moment and refresh, or contact support if your plan did not update.');
      setSearchParams({}, { replace: true });
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, currentOrg, load]);

  const tierCompare = useCallback(
    (slug) => {
      const cur = PLAN_TIER[internalPlanId] ?? 0;
      const tgt = PLAN_TIER[slugToInternal(slug)] ?? 0;
      return tgt - cur;
    },
    [internalPlanId]
  );

  const planPriceDisplay = (plan) => {
    if (plan.id === 'enterprise' || plan.is_enterprise_contact) return null;
    if (billingCycle === 'yearly') {
      const mo = yearlyMonthlyCents(plan.yearly_price_cents);
      return {
        main: formatUsdFromCents(mo, { decimals: 0 }),
        suffix: '/mo',
        note: 'billed annually',
        savings: savingsPercent(plan.monthly_price_cents, plan.yearly_price_cents),
      };
    }
    return {
      main: formatUsdFromCents(plan.monthly_price_cents, { decimals: 0 }),
      suffix: '/mo',
      note: null,
      savings: 0,
    };
  };

  const addonPriceCents = (addon) =>
    billingCycle === 'yearly' ? addon.yearly_price_cents : addon.monthly_price_cents;

  const visibleAddons = useMemo(
    () => addonsForPlan(addons, effectivePlanSlug),
    [addons, effectivePlanSlug]
  );

  const isAddonActive = (addonId) => isCatalogAddonActive(subscription, addonId);

  useEffect(() => {
    if (!visibleAddons.length) return;
    setAddonQuantities((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const a of visibleAddons) {
        if (!a.is_quantity_based || next[a.id] !== undefined) continue;
        next[a.id] = 0;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleAddons, subscription]);

  const planAutoRenewOn = subscription?.auto_renew_enabled !== false;

  const handleAddonAutoRenew = async (addonId, enabled) => {
    if (!currentOrg) return;
    if (enabled && !planAutoRenewOn) {
      toast.error('Enable plan auto-renew first');
      return;
    }
    setActionLoading(true);
    try {
      await setAddonAutoRenew(currentOrg.id, addonId, enabled);
      toast.success(enabled ? 'Add-on auto-renew enabled' : 'Add-on auto-renew disabled');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update auto-renew');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlanAutoRenew = async (enabled) => {
    if (!currentOrg) return;
    setActionLoading(true);
    try {
      await setPlanAutoRenew(currentOrg.id, enabled);
      toast.success(enabled ? 'Plan auto-renew enabled' : 'Plan auto-renew disabled');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update auto-renew');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleAddon = (addonId, on) => {
    const addon = addons.find((a) => a.id === addonId);
    if (isAddonActive(addonId) && !addon?.is_quantity_based) return;
    setSelectedAddons((prev) => ({ ...prev, [addonId]: on }));
    if (!on && addon?.is_quantity_based) {
      setAddonQuantities((prev) => ({ ...prev, [addonId]: 0 }));
    }
  };

  const setQty = (addonId, qty) => {
    const addon = addons.find((a) => a.id === addonId);
    const max = addon?.max_quantity || 500;
    const owned = ownedAddonQuantity(subscription, addonId);
    const maxAdd = addon?.is_quantity_based ? Math.max(0, max - owned) : max;
    setAddonQuantities((prev) => ({
      ...prev,
      [addonId]: Math.max(0, Math.min(maxAdd, qty)),
    }));
  };

  const handleCancelAddon = async (addonId) => {
    if (!currentOrg) return;
    if (
      !window.confirm(
        'Cancel this add-on at the end of its billing period? It stays active until then.'
      )
    ) {
      return;
    }
    setActionLoading(true);
    try {
      await cancelCatalogAddon(currentOrg.id, addonId);
      toast.success('Add-on will cancel at period end');
      await load();
      setSelectedAddons((prev) => ({ ...prev, [addonId]: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel add-on');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlanCta = async (slug) => {
    if (slug === 'enterprise') {
      const email = displaySettings.contact_sales_email || 'sales@doc-tree.com';
      window.location.href = `mailto:${email}?subject=Enterprise%20plan%20inquiry`;
      return;
    }
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    if (!currentOrg) {
      toast.error('Select a workspace first');
      return;
    }

    const cmp = tierCompare(slug);
    // While a plan is lapsed every tier is a fresh full-price purchase, including the
    // one the org nominally holds — route it all to checkout instead of upgrade/downgrade.
    if (renewalRequired) {
      setSelectedPlanSlug(slug);
      return;
    }
    if (cmp === 0) return;

    if (cmp < 0) {
      setDowngradeTarget(slug);
      setActionLoading(true);
      setDowngradeOpen(true);
      try {
        const preview = await previewDowngrade(currentOrg.id, slug);
        setDowngradePreview(preview);
      } catch {
        setDowngradePreview(null);
      } finally {
        setActionLoading(false);
      }
      return;
    }

    setSelectedPlanSlug(slug);

    if (cmp > 0 && internalPlanId !== 'free' && !isTrialing) {
      setUpgradeTarget(slug);
      setActionLoading(true);
      setUpgradeOpen(true);
      try {
        const preview = await previewProration(currentOrg.id, {
          planSlug: slug,
          billingCycle,
        });
        setUpgradePreview(preview);
      } catch {
        setUpgradePreview(null);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const confirmUpgrade = async () => {
    if (!currentOrg || !upgradeTarget) return;
    setActionLoading(true);
    try {
      if (paymentsEnabled && !stripeEnabled) {
        const { pay_url } = await startSkipCashPayment(currentOrg.id, {
          product_type: 'plan',
          plan_id: slugToInternal(upgradeTarget),
          billing_cycle: billingCycle,
        });
        if (pay_url) window.location.href = pay_url;
        return;
      }
      await upgradePlan(currentOrg.id, upgradeTarget, billingCycle);
      toast.success('Plan upgraded');
      setUpgradeOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Upgrade failed');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDowngrade = async () => {
    if (!currentOrg || !downgradeTarget) return;
    setActionLoading(true);
    try {
      await scheduleDowngrade(currentOrg.id, downgradeTarget, billingCycle);
      toast.success('Downgrade scheduled for end of billing period');
      setDowngradeOpen(false);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === 'string' ? d : 'Downgrade failed'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="max-w-lg mx-auto py-20 px-4 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">You&apos;re all set!</h1>
        <p className="text-slate-600 dark:text-slate-400">
          Payment successful · {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} billing
        </p>
        <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-10">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-3">
            <h1 className="text-3xl md:text-4xl font-bold font-[Manrope] text-slate-900 dark:text-white">
              Simple, transparent pricing
            </h1>
            {user && currentOrg && (
              <BillingHistoryDialog organizationId={currentOrg.id} />
            )}
          </div>
          <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
            {isTrialing
              ? `You're on a free trial. Upgrade to Growing or Scaling — pay monthly or yearly.`
              : `Choose Growing, Scaling, or Enterprise. All plans include a ${freeTrialDays}-day free trial.`}
          </p>
        </div>

        {renewalRequired && (
          <div className="max-w-2xl mx-auto mb-10 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700/60 dark:bg-amber-950/40">
            <p className="font-medium text-amber-900 dark:text-amber-200">
              Your {subscription?.plan_name || 'plan'} subscription ended on{' '}
              {subscription?.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString()
                : 'its renewal date'}
              .
            </p>
            <p className="text-amber-800 dark:text-amber-300/90">
              Renew below to restore published documentation and custom domains. You can also
              switch to a different plan — either way you pay the full price for a new period.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {plans.map((plan) => {
                const price = planPriceDisplay(plan);
                const cmp = tierCompare(plan.id);
                const isCurrent = cmp === 0;
                const isSelected = effectivePlanSlug === plan.id;
                const cta = planCtaLabel({
                  slug: plan.id,
                  planName: plan.name,
                  internalPlanId,
                  isTrialing,
                  tierCompare: cmp,
                  renewalRequired,
                });
                const features = includedFeatures(plan);

                return (
                  <Card
                    key={plan.id}
                    className={`relative flex flex-col cursor-pointer transition ${
                      isSelected
                        ? 'border-blue-500 ring-2 ring-blue-500/30'
                        : plan.is_highlighted
                          ? 'border-blue-500 shadow-lg'
                          : 'border-slate-200 dark:border-slate-800'
                    }`}
                    onClick={() => plan.id !== 'enterprise' && setSelectedPlanSlug(plan.id)}
                  >
                    {plan.is_highlighted && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600">
                        Most Popular
                      </Badge>
                    )}
                    <CardHeader>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription>{plan.tagline}</CardDescription>
                      <div className="pt-4 min-h-[4rem]">
                        {plan.id === 'enterprise' || plan.is_enterprise_contact ? (
                          <p className="text-2xl font-semibold">Custom pricing</p>
                        ) : price ? (
                          <>
                            <p className="text-3xl font-bold">
                              {price.main}
                              <span className="text-base font-normal text-slate-500">{price.suffix}</span>
                            </p>
                            {price.note && (
                              <p className="text-xs text-slate-500 mt-1">{price.note}</p>
                            )}
                            {price.savings > 0 && billingCycle === 'yearly' && (
                              <Badge variant="secondary" className="mt-2">
                                Save {price.savings}%
                              </Badge>
                            )}
                          </>
                        ) : null}
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <ul className="space-y-2 text-sm">
                        {features.map((f) => (
                          <li key={f.id} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span className="text-slate-700 dark:text-slate-300">
                              {f.label || f.id}
                              {f.note ? ` (${f.note})` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className="w-full"
                        variant={
                          isCurrent && !renewalRequired
                            ? 'outline'
                            : isSelected
                              ? 'default'
                              : 'secondary'
                        }
                        disabled={(isCurrent && !renewalRequired) || actionLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlanCta(plan.id);
                        }}
                      >
                        {cta}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>

            {showAddonsSection(isEnterprise ? null : internalPlanId) && visibleAddons.length > 0 && (
              <section>
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                      Enhance your plan with add-ons
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mt-1">
                      Prices below reflect your selected billing cycle.
                    </p>
                  </div>
                  <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-700 p-0.5 shrink-0">
                    <button
                      type="button"
                      className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                        billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'text-slate-600'
                      }`}
                      onClick={() => setBillingCycle('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                        billingCycle === 'yearly' ? 'bg-blue-600 text-white' : 'text-slate-600'
                      }`}
                      onClick={() => setBillingCycle('yearly')}
                    >
                      Yearly
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {visibleAddons.map((addon) => {
                    const active = isAddonActive(addon.id);
                    const addonStatus = getCatalogAddonStatus(subscription, addon.id);
                    const cents = addonPriceCents(addon);
                    const owned = ownedAddonQuantity(subscription, addon.id);
                    const qty = addon.is_quantity_based
                      ? (addonQuantities[addon.id] ?? 0)
                      : (addonQuantities[addon.id] ?? 1);

                    return (
                      <Card
                        key={addon.id}
                        className={`border-slate-200 dark:border-slate-800 ${
                          active ? 'ring-1 ring-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-950/10' : ''
                        }`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-violet-500" />
                              {addon.name}
                            </CardTitle>
                            {active && (
                              <Badge className="shrink-0 bg-emerald-600 hover:bg-emerald-600">
                                {addon.is_quantity_based && owned > 0
                                  ? `Active · ${owned} purchased`
                                  : 'Active & paid'}
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="text-sm">{addon.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="font-semibold">
                            {addon.is_tiered_quantity ? (
                              <>
                                {formatUsdFromCents(addonPriceCents(addon))}
                                <span className="text-sm font-normal text-slate-500">
                                  {billingCycle === 'yearly' ? '/yr' : '/mo'} · includes 1 language
                                </span>
                                <span className="block text-xs font-normal text-slate-500 mt-1">
                                  +{formatUsdFromCents(tieredAddonUnitCents(addon, billingCycle), { decimals: 0 })}
                                  {billingCycle === 'yearly' ? '/yr' : '/mo'} each additional language
                                </span>
                              </>
                            ) : (
                              <>
                                {formatUsdFromCents(cents)}
                                <span className="text-sm font-normal text-slate-500">
                                  {billingCycle === 'yearly' ? '/yr' : '/mo'}
                                </span>
                              </>
                            )}
                          </p>
                          {addon.is_quantity_based && (
                            <div className="space-y-1">
                              {active && (
                                <p className="text-xs text-slate-500">
                                  {addon.id === 'extra_team_members'
                                    ? `You have ${owned} extra seat${owned !== 1 ? 's' : ''}.`
                                    : addon.id === 'faq_widget'
                                      ? `You have ${owned} FAQ widget${owned !== 1 ? 's' : ''}.`
                                      : addon.id === 'translation_localization'
                                        ? `You have ${owned} language${owned !== 1 ? 's' : ''} included.`
                                        : `You have ${owned} extra domain${owned !== 1 ? 's' : ''}.`}
                                </p>
                              )}
                              <Label className="text-xs text-slate-500">
                                {active ? 'Add more to purchase' : 'Quantity'}
                              </Label>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  disabled={qty <= 0}
                                  onClick={() => setQty(addon.id, qty - 1)}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className="w-8 text-center text-sm font-medium">{qty}</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setQty(addon.id, qty + 1)}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                              {qty > 0 && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  {active ? '+' : ''}
                                  {qty} in checkout (
                                  {formatUsdFromCents(
                                    addon.is_tiered_quantity
                                      ? addonLineAmountCents(addon, qty, owned, billingCycle)
                                      : cents * qty,
                                    { decimals: 2 }
                                  )}
                                  {billingCycle === 'yearly' ? '/yr' : '/mo'})
                                </p>
                              )}
                            </div>
                          )}
                          {active ? (
                            <div className="space-y-3">
                              {addonStatus?.period_end && !addon.is_quantity_based && (
                                <p className="text-xs text-slate-500">
                                  Active until {new Date(addonStatus.period_end).toLocaleDateString()}
                                  {addonStatus.cancel_at_period_end ? ' · Cancels at period end' : ''}
                                </p>
                              )}
                              <div className="flex items-center justify-between gap-2">
                                <Label htmlFor={`addon-ar-${addon.id}`} className="text-sm">
                                  Auto-renew
                                </Label>
                                <Switch
                                  id={`addon-ar-${addon.id}`}
                                  checked={addonStatus?.auto_renew_enabled !== false}
                                  disabled={actionLoading || !planAutoRenewOn}
                                  onCheckedChange={(v) => handleAddonAutoRenew(addon.id, v)}
                                />
                              </div>
                              {!planAutoRenewOn && (
                                <p className="text-xs text-amber-600">Enable plan auto-renew first</p>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 w-full"
                                disabled={actionLoading || addonStatus?.cancel_at_period_end}
                                onClick={() => handleCancelAddon(addon.id)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                {addonStatus?.cancel_at_period_end
                                  ? 'Cancellation scheduled'
                                  : 'Cancel at period end'}
                              </Button>
                            </div>
                          ) : (
                            !addon.is_quantity_based && (
                              <div className="flex items-center justify-between">
                                <Label htmlFor={`addon-${addon.id}`} className="text-sm">
                                  Add to checkout
                                </Label>
                                <Switch
                                  id={`addon-${addon.id}`}
                                  checked={!!selectedAddons[addon.id]}
                                  onCheckedChange={(v) => toggleAddon(addon.id, v)}
                                />
                              </div>
                            )
                          )}
                          {!active && addon.is_quantity_based && qty > 0 && (
                            <p className="text-xs text-slate-500">
                              Included in checkout when quantity &gt; 0
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          <PricingCheckoutPanel
            selectedPlan={selectedPlan}
            billingCycle={billingCycle}
            onBillingCycleChange={setBillingCycle}
            addons={visibleAddons}
            selectedAddons={selectedAddons}
            addonQuantities={addonQuantities}
            subscription={subscription}
            internalPlanId={internalPlanId}
            isTrialing={isTrialing}
            organizationId={currentOrg?.id}
            paymentsEnabled={paymentsEnabled}
            stripeEnabled={stripeEnabled}
            user={user}
            paying={paying}
            setPaying={setPaying}
            planAutoRenewOn={planAutoRenewOn}
            onPlanAutoRenewChange={handlePlanAutoRenew}
            actionLoading={actionLoading}
            renewalRequired={renewalRequired}
          />
        </div>
      </div>

      <UpgradePlanDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        loading={actionLoading}
        currentPlan={plans.find((p) => p.id === currentSlug)}
        targetPlan={plans.find((p) => p.id === upgradeTarget)}
        preview={upgradePreview}
        onConfirm={confirmUpgrade}
      />

      <DowngradePlanDialog
        open={downgradeOpen}
        onOpenChange={setDowngradeOpen}
        loading={actionLoading}
        targetPlan={plans.find((p) => p.id === downgradeTarget)}
        preview={downgradePreview}
        onConfirm={confirmDowngrade}
      />
    </div>
  );
}
